import React, { useEffect, useState } from "react";
import "./LeaderBoard.css";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import BottomNav from "../BottomNav";
import { subscribeToLeaderboard } from "../utils/pulseService";
import { supabase } from "./supabaseClient";
import PulseIcon from "../assets/pulse.png";

const ONE_HOUR = 60 * 60;

const Leaderboard = () => {
  const navigate = useNavigate();
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeToLeaderboard(10, async (users) => {
      const usersWithImages = await Promise.all(
        users.map(async (user) => {
          let profileImageUrl = null;

          if (user.profileImage) {
            try {
              const { data: signed, error } = await supabase.storage
                .from("images")
                .createSignedUrl(user.profileImage, ONE_HOUR);
              if (!error) profileImageUrl = signed.signedUrl;
            } catch (err) {
              console.error("Error fetching profile image:", err);
            }
          } else if (user.id) {
            try {
              const { data: files, error } = await supabase.storage
                .from("images")
                .list(`profile-pics/${user.id}`, {
                  limit: 1,
                  sortBy: { column: "created_at", order: "desc" },
                });

              if (!error && files && files.length > 0) {
                const profilePath = `profile-pics/${user.id}/${files[0].name}`;
                const { data: signed, error: signError } = await supabase.storage
                  .from("images")
                  .createSignedUrl(profilePath, ONE_HOUR);
                if (!signError) profileImageUrl = signed.signedUrl;
              }
            } catch (err) {
              console.error("Error listing profile images:", err);
            }
          }

          return { ...user, profileImageUrl };
        })
      );

      setTopUsers(usersWithImages);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const goToJourney = (id) => navigate(`/journey/${id}`);

  const renderAvatar = (user, className) =>
    user.profileImageUrl ? (
      <img
        src={user.profileImageUrl}
        alt={user.authorName || "User"}
        className={className}
      />
    ) : (
      <div className={`${className} leaderboard-avatar-fallback`}>
        {(user.authorName || user.email || "?").charAt(0).toUpperCase()}
      </div>
    );

  // Visual left-to-right order on the podium: rank 2, rank 1, rank 3
  const podiumOrder = [1, 0, 2];
  const top3 = topUsers.slice(0, 3);
  const rest = topUsers.slice(3);

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">
        <FaArrowLeft className="leaderboard-back-icon" onClick={() => navigate(-1)} />
        <h2 className="leaderboard-title">Leaderboard</h2>
      </div>


      {loading ? (
        <div className="leaderboard-loading">Loading leaderboard...</div>
      ) : topUsers.length === 0 ? (
        <div className="leaderboard-empty">No users with pulse points yet!</div>
      ) : (
        <>
          {/* Podium — top 3 */}
          <div className="leaderboard-podium">
            {podiumOrder.map((idx) => {
              const user = top3[idx];
              if (!user) return null;
              const rank = idx + 1;
              return (
                <div
                  key={user.id}
                  className={`leaderboard-podium-card leaderboard-podium-card--rank-${rank}`}
                  onClick={() => goToJourney(user.id)}
                >
                  <span className="leaderboard-poster-rank-stamp">{rank}</span>

                  <div className="leaderboard-poster-photo-frame">
                    {renderAvatar(user, "leaderboard-podium-avatar")}
                  </div>
                  <span className="leaderboard-podium-name">
                    {user.authorName || user.email || "Anonymous"}
                  </span>
                  <div className="leaderboard-poster-reward-line">
                    <span className="leaderboard-poster-reward-label">Pulse</span>
                    <div className="leaderboard-podium-pulse">
                      <span className="leaderboard-podium-pulse-count">{user.pulse || 0}</span>
                      <img src={PulseIcon} alt="Pulse" className="leaderboard-pulse-icon" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Ledger — rank 4 and below */}
          <div className="leaderboard-ledger">
            {rest.map((user, i) => (
              <div
                key={user.id}
                className="leaderboard-ledger-row"
                onClick={() => goToJourney(user.id)}
              >
                <span className="leaderboard-ledger-rank">
                  {String(i + 4).padStart(2, "0")}
                </span>
                {renderAvatar(user, "leaderboard-ledger-avatar")}
                <span className="leaderboard-ledger-name">
                  {user.authorName || user.email || "Anonymous"}
                </span>
                <div className="leaderboard-ledger-pulse">
                  <span className="leaderboard-ledger-pulse-count">{user.pulse || 0}</span>
                  <img src={PulseIcon} alt="Pulse" className="leaderboard-pulse-icon" />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <BottomNav />
    </div>
  );
};

export default Leaderboard;